/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import {
    createLogPrefix,
    type LogTarget,
    logDebug,
    type ReadonlyAbort,
    type Unsubscribe,
} from '@amazon/vinyl-util'
import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { AGENT_API_KEY, WS_CAPABILITIES, WS_CHANNEL } from '@/blackbolt/agent/claw/clawConfig'
import { getAgentBaseUrl } from '@/blackbolt/agent/claw/clawHttp'

// Low-level `/agents/claw/ws` WebSocket plumbing: open handshake, the string
// frame reader, and the diagnostic connection-state observable. Split out of
// `clawAdapter` so the adapter orchestrates the turn without owning socket
// mechanics.

const target: LogTarget = { logPrefix: createLogPrefix('clawSocket') }

// Best-effort diagnostic view of the `/agents/claw/ws` socket lifecycle;
// no production path reacts to it (errorHandler still owns failures). One
// module singleton because there is at most one WS session per turn.
export type ConnectionState = 'idle' | 'connecting' | 'open' | 'closed' | 'error'

const connectionState$ = data<ConnectionState>('idle')

/** Read-only view of the current WebSocket connection state (diagnostic). */
export function getConnectionState(): ObservableValue<ConnectionState> {
    return connectionState$
}

function setConnectionState(next: ConnectionState): void {
    if (connectionState$.value !== next) connectionState$.value = next
}

export interface WsAuthParams {
    readonly token: string
    readonly apiKey: string
}

// Browsers can't set custom headers on the WS upgrade, so the AmznMusic
// envelope + api key ride as URL query params instead — without them the
// gateway rejects the upgrade with a bare `error` before `open`.
export async function buildWsAuthParams(): Promise<WsAuthParams> {
    const [{ getSocketAuthToken }, { getDeviceInfo }, { getDeviceConfig }] = await Promise.all([
        import('@/auth/authRef'),
        import('@/device/device'),
        import('@/device/deviceConfig'),
    ])
    const { deviceId } = getDeviceInfo()
    const { deviceTypeId } = getDeviceConfig()

    const oAuthToken = await getSocketAuthToken()
    const token = oAuthToken
        ? `AmznMusic ${btoa(
              JSON.stringify({
                  accessToken: oAuthToken,
                  deviceId,
                  deviceType: deviceTypeId,
              })
          )}`
        : ''
    return { token, apiKey: AGENT_API_KEY }
}

function buildWsUrl(auth: WsAuthParams): string {
    const base = getAgentBaseUrl()
    const params = new URLSearchParams({
        channel: WS_CHANNEL,
        capabilities: WS_CAPABILITIES,
        apiKey: auth.apiKey,
    })
    if (auth.token) params.set('token', auth.token)
    const query = params.toString()

    if (base.startsWith('/')) {
        const wsProto =
            typeof location !== 'undefined' && location.protocol === 'https:' ? 'wss:' : 'ws:'
        const origin = typeof location !== 'undefined' ? location.host : ''
        return `${wsProto}//${origin}${base}/agents/claw/ws?${query}`
    }
    const wsBase = base.replace('https://', 'wss://').replace('http://', 'ws://')
    return `${wsBase}/agents/claw/ws?${query}`
}

export function openSocket(
    abort: ReadonlyAbort | undefined,
    auth: WsAuthParams
): Promise<WebSocket> {
    return new Promise((resolve, reject) => {
        try {
            abort?.throwIfAborted()
        } catch (err) {
            reject(err)
            return
        }
        setConnectionState('connecting')
        const socket = new WebSocket(buildWsUrl(auth))
        let unsubscribeAbort: Unsubscribe | undefined
        const onAbort = (event: { reason: Error }) => {
            try {
                socket.close()
            } catch {
                /* noop */
            }
            setConnectionState('closed')
            reject(event.reason)
        }
        socket.addEventListener(
            'open',
            () => {
                unsubscribeAbort?.()
                setConnectionState('open')
                socket.addEventListener('close', () => setConnectionState('closed'), { once: true })
                socket.addEventListener('error', () => setConnectionState('error'), { once: true })
                resolve(socket)
            },
            { once: true }
        )
        socket.addEventListener(
            'error',
            (event) => {
                unsubscribeAbort?.()
                setConnectionState('error')
                reject(new Error(`WebSocket error: ${event.type}`))
            },
            { once: true }
        )
        unsubscribeAbort = abort?.on('abort', onAbort, { once: true })
        logDebug(target, 'openSocket: connecting')
    })
}

export interface FrameReader {
    iter(): AsyncIterable<string>
}

/**
 * Buffer string frames off a live socket into an async iterable. Attach BEFORE
 * `socket.send(...)` so a fast reply arriving on a microtask between send and
 * the first `for await` is buffered, not lost.
 */
export function openFrameReader(socket: WebSocket, abort: ReadonlyAbort | undefined): FrameReader {
    const state = { closed: false }
    const queue: string[] = []
    let resolveNext: (() => void) | null = null

    const wake = () => {
        if (resolveNext) {
            const r = resolveNext
            resolveNext = null
            r()
        }
    }

    const onMessage = (event: MessageEvent) => {
        if (typeof event.data === 'string') {
            queue.push(event.data)
            wake()
        }
    }
    const onClose = () => {
        state.closed = true
        wake()
    }
    const onError = () => {
        state.closed = true
        wake()
    }
    const onAbort = () => {
        // Stop draining, but DON'T close the socket here: it's shared across
        // turns, so its lifecycle belongs to the connection manager (a turn's
        // lease release decides warm-reuse vs. discard). Closing it here would
        // tear a socket out from under any peer turn still streaming on it.
        state.closed = true
        wake()
    }

    socket.addEventListener('message', onMessage)
    socket.addEventListener('close', onClose)
    socket.addEventListener('error', onError)
    const unsubscribeAbort = abort?.on('abort', onAbort)

    async function* drain(): AsyncGenerator<string, void, void> {
        try {
            while (true) {
                if (queue.length > 0) {
                    yield queue.shift()!
                    continue
                }
                if (state.closed || abort?.aborted()) return
                await new Promise<void>((r) => {
                    resolveNext = r
                })
            }
        } finally {
            socket.removeEventListener('message', onMessage)
            socket.removeEventListener('close', onClose)
            socket.removeEventListener('error', onError)
            unsubscribeAbort?.()
        }
    }

    return {
        iter: () => drain(),
    }
}
